import React from 'react'
import { Form, Link, usePage } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import { AuthLayout } from '@/layouts/AuthLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface PageProps {
  flash: {
    alert?: string
    notice?: string
  }
  [key: string]: unknown
}

function ForgotPasswordPage() {
  const { flash } = usePage<PageProps>().props

  return (
    <Form method="post" action="/users/password">
      {({ errors, processing }: { errors: Record<string, string[]>; processing: boolean }) => (
        <div className="space-y-4">
          {/* Flash notice (e.g. "Reset instructions sent") */}
          {flash?.notice && (
            <div className="rounded-md border border-green-500/50 bg-green-500/10 px-4 py-3 text-sm text-green-700 dark:text-green-400">
              {flash.notice}
            </div>
          )}

          {/* Flash alert */}
          {flash?.alert && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {flash.alert}
            </div>
          )}

          {/* Email field */}
          <div className="space-y-1.5">
            <Label htmlFor="user_email">Email</Label>
            <Input
              id="user_email"
              name="user[email]"
              type="email"
              autoComplete="email"
              placeholder="name@example.com"
              aria-invalid={!!errors['user[email]']}
            />
            {errors['user[email]'] && (
              <p className="text-sm text-destructive mt-1">{errors['user[email]'][0]}</p>
            )}
          </div>

          {/* Submit */}
          <Button type="submit" className="w-full" disabled={processing}>
            {processing ? 'Sending...' : 'Send reset instructions'}
          </Button>

          {/* Back to sign in */}
          <p className="text-center text-sm">
            <Link
              href="/users/sign_in"
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to sign in
            </Link>
          </p>
        </div>
      )}
    </Form>
  )
}

ForgotPasswordPage.layout = (page: React.ReactNode) => (
  <AuthLayout
    title="Forgot your password?"
    description="Enter your email and we'll send you reset instructions"
  >
    {page}
  </AuthLayout>
)

export default ForgotPasswordPage
